import { Button, type Manifest } from "@nodus/ui";
import * as React from "react";
import { Check } from "lucide-react";
import { join as joinCommand } from "shlex";

/** All launch surfaces keep configuration local until the explicit final review action. */
export function CreationSteps({ configure, resources, review, launch, invalid = false, resourcesInvalid = invalid, busy = false, onStepChange, configureLabel = "Configure work", resourcesLabel = "Choose resources", reviewLabel = "Review & launch" }: {
  configure: React.ReactNode; resources?: React.ReactNode; review: React.ReactNode; launch: React.ReactNode;
  configureLabel?: string; resourcesLabel?: string; reviewLabel?: string;
  invalid?: boolean; resourcesInvalid?: boolean; busy?: boolean; onStepChange?: (step: number) => void;
}) {
  const [step, setStep] = React.useState(0);
  const panels = React.useRef<HTMLDivElement>(null);
  const heading = React.useRef<HTMLHeadingElement>(null);
  const move = (next: number) => { setStep(next); onStepChange?.(next); requestAnimationFrame(() => heading.current?.focus()); };
  const advance = (event: React.MouseEvent) => {
    event.preventDefault();
    const fields = panels.current?.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("[data-creation-panel]:not([hidden]) input, [data-creation-panel]:not([hidden]) select, [data-creation-panel]:not([hidden]) textarea");
    for (const field of fields ?? []) {
      if (field.getAttribute("aria-invalid") === "true" || (field.willValidate && !field.validity.valid)) {
        let details = field.closest("details");
        while (details) { details.open = true; details = details.parentElement?.closest("details") ?? null; }
        field.focus();
        field.reportValidity();
        return;
      }
    }
    move(step + 1);
  };
  const hasResources = resources != null;
  const labels = hasResources ? [configureLabel, resourcesLabel, reviewLabel] : [configureLabel, reviewLabel];
  const reviewStep = labels.length - 1;
  return <div className="nd-creation mt-6 min-w-0">
    <ol aria-label="Creation progress" className={`mb-6 grid max-w-3xl gap-3 border-b border-line pb-5 ${hasResources ? "grid-cols-3" : "grid-cols-2"}`}>
      {labels.map((label, i) => (
        <li key={label} className="min-w-0">
          <button type="button" disabled={busy || i > step} aria-current={step === i ? "step" : undefined} onClick={() => move(i)}
            className={`flex min-h-10 w-full flex-col items-start gap-2 rounded-md text-left text-small sm:flex-row sm:items-center ${i === step ? "font-medium text-fg-primary" : "text-fg-muted"}`}>
            <span aria-hidden className={`inline-flex size-6 shrink-0 items-center justify-center rounded-full border text-small tabular-nums ${i <= step ? "border-primary-border bg-primary text-primary-foreground" : "border-line bg-subtle"}`}>
              {i < step ? <Check className="size-3.5" /> : i + 1}
            </span>
            <span>{label}</span>
          </button>
        </li>
      ))}
    </ol>
    <h2 ref={heading} tabIndex={-1} className="sr-only">{labels[step]}</h2>
    <div ref={panels}>
    <div data-creation-panel hidden={step !== 0} className="max-w-3xl rounded-lg border border-line bg-card p-5">{configure}</div>
    {hasResources ? <div data-creation-panel hidden={step !== 1}>{resources}</div> : null}
    <div data-creation-panel hidden={step !== reviewStep} className="max-w-3xl space-y-5 rounded-lg border border-line bg-card p-5">{review}</div>
    </div>
    <div className={`nd-creation-footer mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-5 ${hasResources && step === 1 ? "" : "max-w-3xl"}`}>
      {step > 0 ? <Button type="button" variant="outline" disabled={busy} onClick={()=>move(step-1)}>Back</Button> : <span />}
      {step < reviewStep ? <Button type="button" disabled={busy || (step === 0 ? invalid : resourcesInvalid)} onClick={advance}>{step === 0 && hasResources ? resourcesLabel : "Review"}</Button> : <React.Fragment key="launch-action">{launch}</React.Fragment>}
    </div>
  </div>;
}

const readableLabel = (key: string) => ({ cpu: "vCPU", memory: "RAM", minMemory: "Minimum VRAM", gpu: "GPU", maxCostUSD: "Spending limit (USD)", perRunMaxCostUSD: "Model spending limit per run (USD)", offerings: "Hardware", regions: "Regions", input: "Task", system: "Instructions", allowedKeys: "Allowed API keys", maxParallelAgents: "Maximum subagents" })[key] ?? key.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase());

function ReviewValue({ value }: { value: unknown }) {
  if (value === undefined || value === null || value === "") return <span className="text-fg-muted">Not set</span>;
  if (Array.isArray(value)) {
    if (!value.length) return <span>None</span>;
    if (value.every((item) => item === null || typeof item !== "object")) return <span>{value.map(String).join(", ")}</span>;
    return <ul className="grid gap-3">{value.map((item, index) => <li key={index}><ReviewValue value={item} /></li>)}</ul>;
  }
  if (typeof value === "object") return <dl className="grid gap-2">{Object.entries(value).map(([key, item]) => <div key={key} className="flex flex-wrap gap-x-3"><dt className="text-fg-muted">{readableLabel(key)}</dt><dd className="min-w-0 break-words"><ReviewValue value={item} /></dd></div>)}</dl>;
  return <span>{typeof value === "boolean" ? value ? "Yes" : "No" : String(value)}</span>;
}

export function ManifestReview({ manifest }: { manifest: Manifest }) {
  const spec = manifest.spec as Record<string, unknown>;
  const fields = Object.entries(spec).filter(([key, value]) => !["env", "secrets"].includes(key) && value !== undefined);
  return <section aria-label="Review configuration">
    <h3 className="mb-4 text-section-title font-semibold">{String(manifest.metadata?.name ?? manifest.kind ?? "Configuration")}</h3>
    <dl className="divide-y divide-line-subtle">{fields.map(([key,value])=><div key={key} className="grid gap-1 py-3 sm:grid-cols-[160px_minmax(0,1fr)]"><dt className="text-ui text-fg-muted">{readableLabel(key)}</dt><dd className="min-w-0 max-h-64 overflow-auto whitespace-pre-wrap break-words text-ui"><ReviewValue value={key === "command" && Array.isArray(value) ? joinCommand(value.map(String)) : value} /></dd></div>)}</dl>
  </section>;
}
